import { memo, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { useAppDispatch } from "@zenith/app/hooks";
import { OkTick, USSD } from "@zenith/assets/icons/components";
import { LinearProgressComponent } from "@zenith/components";
import { usePaymentsContext } from "@zenith/context/global-context/hooks";
import {
  EXPIRATION_DATE_FOR_PAYMENTS,
  EXPIRATION_DATE_FOR_PENDING_USSD_CODE_ITEM,
  EXPIRATION_DATE_FOR_USSD_CODE_ITEM,
  MILLISECONDS,
  PERCENTAGE,
  SECONDS,
  SSE_WAITING_MINUTES,
  WAITING_FOR_CONFIRMATION_LABEL,
} from "@zenith/data/constants";
import { emptyCart } from "@zenith/features/Payments/components";
import { PaymentNotConfirmed } from "@zenith/pages/PaymentsPage";
import { DASHBOARD, PAYMENT, PAYMENT_SUCCESS } from "@zenith/routes/paths";
import { OperationDTO } from "@zenith/services/transactions";
import { formatCurrency, formatTimeForCounter } from "@zenith/utils";
import { pollPaymentStatus } from "@zenith/utils/Polls/pollPaymentStatus";
import { useLocation } from "react-router-dom";

const USSDCodePendingPage = memo(function USSDCodePendingPage() {
  const { paymentsContext, clearPaymentsContext, setPaymentsContext } =
    usePaymentsContext();
  const {
    paymentStatus,
    totalAmount,
    operationId,
    transactionInfo: { transactionDate },
  } = paymentsContext;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    (location.state as { errorMessage?: string })?.errorMessage
  );

  const expirationDate = Number(
    localStorage.getItem(EXPIRATION_DATE_FOR_PENDING_USSD_CODE_ITEM)
  );

  const initialRemainingSeconds = Math.floor(
    (expirationDate - new Date().getTime()) / MILLISECONDS
  );
  const [seconds, setSeconds] = useState<number>(initialRemainingSeconds);

  useEffect(() => {
    const locationState = location.state as {
      initialStatus?: OperationDTO.status;
      errorMessage?: string;
    };

    if (locationState?.initialStatus) {
      setPaymentsContext({
        ...paymentsContext,
        paymentStatus: locationState.initialStatus,
      });
    }

    if (locationState?.errorMessage) {
      setErrorMessage(locationState.errorMessage);
    }
  }, [location.state]);

  useEffect(() => {
    const persistedStatus = localStorage.getItem("paymentStatus");
    const persistedOperationId = localStorage.getItem("persistedOperationId");

    if (
      persistedStatus === OperationDTO.status.SUCCESS &&
      persistedOperationId === operationId
    ) {
      setPaymentsContext({
        ...paymentsContext,
        paymentStatus: OperationDTO.status.SUCCESS,
      });
      localStorage.removeItem("paymentStatus");
      localStorage.removeItem("persistedOperationId");
    }
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      const remaining = expirationDate - new Date().getTime();

      if (remaining >= 0) {
        setSeconds(Math.floor(remaining / MILLISECONDS));
      } else {
        clearInterval(timer);
      }
    }, 100);

    return () => clearInterval(timer);
  }, [expirationDate]);

  useEffect(() => {
    if (paymentStatus === OperationDTO.status.SUCCESS) {
      handleSuccessfulPayment();
    }
  }, [paymentStatus]);

  const handleSuccessfulPayment = () => {
    localStorage.removeItem(EXPIRATION_DATE_FOR_PENDING_USSD_CODE_ITEM);
    localStorage.removeItem("paymentStatus");
    localStorage.removeItem("persistedOperationId");

    setTimeout(() => {
      navigate(PAYMENT_SUCCESS, {
        state: {
          operationData: paymentsContext,
          transactionData: paymentsContext.transactionInfo,
          operationId: operationId,
          totalAmount: totalAmount,
          paymentMethod: OperationDTO.paymentMethod.USSD,
          timestamp: new Date().toISOString(),
        },
      });
    }, 1500);
  };

  function isStatusSuccessful(): boolean {
    return paymentStatus === OperationDTO.status.SUCCESS;
  }

  function handleProgressValue(): number {
    if (isStatusSuccessful()) {
      return 100;
    }

    const date = new Date();

    const currentTime =
      (expirationDate - date.getTime()) / MILLISECONDS / SECONDS;

    const percentage =
      ((SSE_WAITING_MINUTES - currentTime) / SSE_WAITING_MINUTES) * PERCENTAGE;

    if (percentage < 0) {
      return 0;
    }

    if (percentage >= 100) {
      return 100;
    }

    return percentage;
  }

  function clearContext(): void {
    clearPaymentsContext();

    localStorage.removeItem(EXPIRATION_DATE_FOR_USSD_CODE_ITEM);
    localStorage.removeItem(EXPIRATION_DATE_FOR_PENDING_USSD_CODE_ITEM);
    localStorage.removeItem("paymentStatus");
    localStorage.removeItem("persistedOperationId");

    dispatch(emptyCart());
  }

  const handleGoToDashboard = () => {
    clearContext();
    navigate(DASHBOARD);
  };

  const handleReconfirmPayment = async () => {
    if (!operationId) return;

    try {
      const response = await pollPaymentStatus(operationId, false);
      setErrorMessage(undefined);

      if (response?.data) {
        setPaymentsContext({
          ...paymentsContext,
          ...response.data,
          paymentStatus: response.status,
          operationDetails: response.data,
          transactionInfo: {
            ...paymentsContext.transactionInfo,
            ussdCode: response.data.ussd?.operationCode,
          },
        });

        if (response.status === OperationDTO.status.SUCCESS) {
          localStorage.setItem("paymentStatus", response.status);
          localStorage.setItem("persistedOperationId", operationId);
          return;
        }
      } else if (response?.error) {
        setErrorMessage(response.error.message);
      }

      if (response.status !== OperationDTO.status.SUCCESS) {
        const expirationDateForUssd = Date.now() + EXPIRATION_DATE_FOR_PAYMENTS;
        localStorage.setItem(
          EXPIRATION_DATE_FOR_PENDING_USSD_CODE_ITEM,
          expirationDateForUssd.toString()
        );
        setSeconds(expirationDateForUssd - Date.now());
      }
    } catch (error) {
      console.error("Error reconfirming payment:", error);
      setErrorMessage("An error occurred while checking payment status");
    }
  };

  return (
    <div className="mt-12 mb-16 flex items-center justify-center">
      {paymentStatus !== OperationDTO.status.SUCCESS && seconds <= 0 ? (
        <PaymentNotConfirmed
          chargedAmount={totalAmount as number}
          date={new Date(transactionDate || Date.now())}
          handleGoToDashboard={handleGoToDashboard}
          handleReconfirmPayment={handleReconfirmPayment}
        />
      ) : (
        <div className="flex w-120 flex-col items-center rounded-2.5xl border border-neutral-200 px-8 pt-6 pb-8">
          <h1 className="m-20 text-2xl font-medium text-neutral-900">
            USSD
          </h1>
          <div
            className={`flex h-40 w-40 items-center justify-center rounded-full ${
              isStatusSuccessful() ? "bg-accent-success" : "bg-primary-50"
            }`}
          >
            {isStatusSuccessful() ? (
              <OkTick className="h-20 w-20" />
            ) : (
              <USSD className="h-20 w-20" />
            )}
          </div>
          <div className="m-20 inline-flex h-14 w-80 flex-col items-center justify-center gap-1">
            <div className="text-base font-normal leading-normal text-neutral-500">
              Amount
            </div>
            <div className="text-right text-2xl font-medium leading-loose text-neutral-900">
              {formatCurrency(totalAmount)}
            </div>

            <div className="w-80">
              <LinearProgressComponent
                className="h-1.5"
                primaryColor="#F3F3F3"
                secondaryColor={isStatusSuccessful() ? "#6EBD7F" : "#9B9B9B"}
                value={handleProgressValue()}
              />
              {!isStatusSuccessful() ? (
                <div className="mt-5 flex items-center justify-center text-sm font-normal leading-none">
                  <span className="text-zinc-600">
                    {WAITING_FOR_CONFIRMATION_LABEL}{" "}
                    <strong>{formatTimeForCounter(seconds, "minutes")}</strong>
                  </span>
                </div>
              ) : (
                <div className="mt-5 flex items-center justify-center text-sm font-normal leading-none">
                  <div className="text-green-600">Completed!</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default USSDCodePendingPage;